import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { StorageService, UtilsService } from 'src/app/services';
import { images } from '../../../../images';
import { ExpeditionStatus, UserRole } from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { IBulkActionType } from 'src/app/models/enums/bulkActionType';
import { IBooking } from 'src/app/models';

@Component({
    selector: 'app-booking-search-bar',
    templateUrl: './booking-search-bar.component.html',
    styleUrl: './booking-search-bar.component.scss',
    standalone: false
})
export class BookingSearchBarComponent implements OnInit, OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;

  @Input() placeholder = '';
  @Input() searching = false;
  @Input() bookings: IBooking[] = [];
  @Input() searchValue = '';
  @Output() addBookingEventEmitter = new EventEmitter();
  @Output() filter = new EventEmitter();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() sort = new EventEmitter();
  @Output() enableMultiselect = new EventEmitter<string>();
  @Output() onBulkAction = new EventEmitter();
  @Output() onChangePaginationSize = new EventEmitter<number>();

  public filterOpened = false;
  public filtersApplied = 0;
  public allowBulkEditing = true;

  public sortedOpened = false;

  public rowsDisplayOpened = false;
  public rowsDisplayValue = APP.DEFAULT_PAGE_SIZE;
  public defaultRowsDisplay = APP.DEFAULT_PAGE_SIZE;

  public bulkActions = [
    {
      action: IBulkActionType.EDIT,
      label: 'bookings.actions.reassignBookings.title',
      image: images.booking.actions.bulkSelect
    },
    {
      action: IBulkActionType.DELETE,
      label: 'bookings.actions.deleteBooking.bulkAction',
      image: images.booking.actions.delete
    }
  ]

  public bulkSelectedAction = '';

  public images = images;

  public adminRole: UserRole;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) { }

  async ngOnInit() {
    this.adminRole = await this.storageService.getRole();
    this.applyPageSizeFromUrl();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => this.applyPageSizeFromUrl());
    this.updateFilters();
  }

  private applyPageSizeFromUrl() {
    const sizeParam = this.activatedRoute.snapshot.queryParamMap.get('size');
    const sizeFromUrl = sizeParam ? Number(sizeParam) : null;
    this.rowsDisplayValue = (sizeFromUrl && !isNaN(sizeFromUrl) && sizeFromUrl > 0)
      ? sizeFromUrl
      : this.defaultRowsDisplay;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searching'] && changes['searching'].currentValue) {
      this.bulkSelectedAction = '';
      this.enableMultiselect.emit('');
    }
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  search(event: any) {
    this.updateFilters();
    this.searchEventEmitter.emit(event);
  }

  async updateFilters() {
    const defaultStatus = [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING];
    const filters = JSON.parse(await this.storageService.getBookingsFilters());
    if (filters) {
      const filtersForCount = { ...filters };
      const isDefaultStatus = filtersForCount.selectedStatus?.length === defaultStatus.length
        && defaultStatus.every((s: ExpeditionStatus) => filtersForCount.selectedStatus.includes(s));
      if (isDefaultStatus || !filtersForCount.selectedStatus?.length) {
        filtersForCount.selectedStatus = [];
      }
      this.filtersApplied = this.utilsService.getFiltersCount(filtersForCount);
    } else {
      this.filtersApplied = 0;
    }
    if (filters && filters.cancelled || filters && filters.faults || filters && filters.selectedStatus.includes(ExpeditionStatus.CLOSED)) this.allowBulkEditing = false;
    else this.allowBulkEditing = true;
  }

  onActionChange(action: string) {
    this.bulkSelectedAction = action;
    this.enableMultiselect.emit(action);
  }

  updateNumberOfElements(event: number) {
    this.rowsDisplayOpened = false;
    this.rowsDisplayValue = event > 0 ? event : this.defaultRowsDisplay;
    this.storageService.setBookingsPageSize(this.rowsDisplayValue);
    this.onChangePaginationSize.emit(event);
  }

  showActionButton(action: string) {
    return this.bulkSelectedAction === action && this.bookings.filter((booking) => booking.selected).length > 0;
  }
}
